import { useState } from 'react';

import { DocumentPicker } from '../../../../conversations/components/DocumentPicker';
import { ModalBase } from '../../../components/modals/ModalBase';

import type { DocumentSummary } from '../../../../../lib/types';
import type React from 'react';

export interface ForensicApprovalModalProps {
  open: boolean;
  /** Tipo 8 (h_c_adversarial_strict) sempre cai em dupla aprovação. */
  dualApproval: boolean;
  /** bondId derivado do override (contextResourceType==='bond' ? contextResourceId : undefined). */
  bondId: string | undefined;
  isPending: boolean;
  failed: boolean;
  outcome: string | null;
  onClose: () => void;
  onConfirm: (args: { documentationDocumentIds: string[]; approvalNote?: string }) => void;
}

/** Tipos 6/8 — documentação obrigatória (1..50 ids). Reusa o DocumentPicker (D5). */
export function ForensicApprovalModal({
  open,
  dualApproval,
  bondId,
  isPending,
  failed,
  outcome,
  onClose,
  onConfirm,
}: ForensicApprovalModalProps): React.JSX.Element | null {
  const [docs, setDocs] = useState<{ id: string; name: string }[]>([]);
  const [note, setNote] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  if (!open) return null;

  const canSubmit = docs.length >= 1 && docs.length <= 50 && !isPending;

  const addDoc = (d: DocumentSummary): void => {
    setPickerOpen(false);
    setDocs((prev) => (prev.some((x) => x.id === d.id) ? prev : [...prev, { id: d.id, name: d.name }]));
  };
  const removeDoc = (id: string): void => {
    setDocs((prev) => prev.filter((x) => x.id !== id));
  };

  return (
    <ModalBase
      open={open}
      onClose={onClose}
      title={dualApproval ? 'Aprovar (documentação + dupla aprovação)' : 'Aprovar com documentação'}
      width={560}
      footer={
        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
          <button className="btn-ghost-mini" type="button" onClick={onClose} disabled={isPending}>
            Cancelar
          </button>
          <button
            className="btn-ghost-mini btn-ghost-mini--primary"
            type="button"
            disabled={!canSubmit}
            onClick={() =>
              onConfirm({
                documentationDocumentIds: docs.map((d) => d.id),
                ...(note.trim() === '' ? {} : { approvalNote: note.trim() }),
              })
            }
          >
            {isPending ? 'Aprovando…' : dualApproval ? 'Aprovar e enviar p/ gestor' : 'Aprovar'}
          </button>
        </div>
      }
    >
      <div className="modal-form">
        {dualApproval && (
          <div
            className="modal-form__callout modal-form__callout--warn"
            style={{ fontSize: 'var(--text-xs)' }}
          >
            ⚠ Exceção forensic · exige documentação + 2ª aprovação do gestor.
          </div>
        )}
        <div className="modal-form__field">
          <span className="modal-form__label">Documentação comprobatória (obrigatória · 1+ documento)</span>
          {docs.length === 0 && (
            <span className="modal-form__hint">Nenhum documento selecionado.</span>
          )}
          {docs.length > 0 && (
            <ul style={{ listStyle: 'none', margin: '4px 0', padding: 0, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {docs.map((d) => (
                <li key={d.id} className="chip">
                  <span className="mono">{d.name}</span>
                  <button
                    type="button"
                    className="chip__caret"
                    aria-label={`Remover ${d.name}`}
                    onClick={() => removeDoc(d.id)}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}
          <button
            type="button"
            className="btn-ghost-mini"
            style={{ alignSelf: 'flex-start' }}
            onClick={() => setPickerOpen(true)}
          >
            + Adicionar documento
          </button>
        </div>
        <label className="modal-form__field">
          <span className="modal-form__label">Nota de aprovação (opcional)</span>
          <textarea
            className="modal-form__textarea"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
          />
        </label>
        {failed && (
          <div className="banner banner--danger" role="alert">
            <div className="banner__body">
              <span className="banner__title">Não foi possível aprovar o override.</span>
            </div>
          </div>
        )}
        {outcome !== null && (
          <div className="modal-form__callout" role="status">
            {outcome}
          </div>
        )}
      </div>

      <DocumentPicker
        open={pickerOpen}
        bondId={bondId}
        onClose={() => setPickerOpen(false)}
        onConfirm={addDoc}
      />
    </ModalBase>
  );
}
